import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  calculateOnboardingProgress,
  getStoredPayoutSkipCompletion,
  getStoredShareCompletion,
  getStoredTimeZoneConfirmation,
  loadOnboardingSteps,
  ONBOARDING_STEPS_CHANGED_EVENT,
  storePayoutSkipCompletion,
  storeShareCompletion,
  storeTimeZoneConfirmation,
  updateBusinessTimeZone,
} from "../utils/proOnboarding";

const buildInitialSteps = (userId, { teamSettingsRequired = false, activeBusinessId = null } = {}) => ({
  services: false,
  availability: false,
  payouts: false,
  payoutsSkipped: getStoredPayoutSkipCompletion(userId, activeBusinessId),
  shareProfile: getStoredShareCompletion(userId, activeBusinessId),
  profileGuidelines: false,
  profileDescription: false,
  workLocationSetup: false,
  appearance: false,
  bookingQuestions: false,
  teamSettings: false,
  teamSettingsDraftSaved: false,
  teamSettingsRequired,
  teamPaymentRequired: false,
  teamPaymentContext: null,
  businessTimeZone: null,
  canManageBusinessTimeZone: false,
  confirmedTimeZone: getStoredTimeZoneConfirmation(userId, activeBusinessId),
});

// Loads when it mounts (or its workspace changes), when `refresh` is called,
// such as when the widget is opened, and when a page saves something that can
// change a step (notifyOnboardingStepsChanged). It does not re-check on every
// route change: see loadOnboardingSteps.
const useProfessionalOnboardingProgress = ({
  userId = null,
  userUsername = null, // Included for API parity and future profile-link actions.
  enabled = true,
  activeBusinessId = null,
  providerId = null,
  teamMode = false,
  businessRole = null,
} = {}) => {
  const teamSettingsRequired = Boolean(teamMode && businessRole === "owner");
  const [steps, setSteps] = useState(() => buildInitialSteps(userId, {
    teamSettingsRequired,
    activeBusinessId,
  }));
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState("");
  const requestIdRef = useRef(0);

  void userUsername;

  const progressMeta = useMemo(() => calculateOnboardingProgress(steps), [steps]);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!userId) {
      setSteps(buildInitialSteps(userId, { teamSettingsRequired, activeBusinessId }));
      setError("");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const onboardingSteps = await loadOnboardingSteps(userId, {
        activeBusinessId,
        providerId,
        teamMode,
        businessRole,
      });

      if (requestIdRef.current === requestId) {
        setSteps(onboardingSteps);
      }
    } catch (err) {
      if (requestIdRef.current === requestId) {
        setError(err.message || "We couldn't load your onboarding progress. Please try again.");
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [activeBusinessId, businessRole, enabled, providerId, teamMode, teamSettingsRequired, userId]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    refresh();
    // The check is shared with other callers, so it runs on; this caller just
    // stops listening for it.
    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, refresh]);

  // A finished onboarding is kept for the session (see loadOnboardingSteps), so
  // once it is finished there is nothing left for a save to change.
  const isComplete = progressMeta.isComplete;
  useEffect(() => {
    if (!enabled || isComplete || typeof window === "undefined") {
      return undefined;
    }

    const handleStepsChanged = () => {
      refresh();
    };

    window.addEventListener(ONBOARDING_STEPS_CHANGED_EVENT, handleStepsChanged);
    return () => window.removeEventListener(ONBOARDING_STEPS_CHANGED_EVENT, handleStepsChanged);
  }, [enabled, isComplete, refresh]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    storeShareCompletion(steps.shareProfile, userId, activeBusinessId);
  }, [activeBusinessId, enabled, steps.shareProfile, userId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    storePayoutSkipCompletion(steps.payoutsSkipped, userId, activeBusinessId);
  }, [activeBusinessId, enabled, steps.payoutsSkipped, userId]);

  const markShareProfileComplete = useCallback(() => {
    if (!enabled) {
      return;
    }
    setSteps((prev) => ({ ...prev, shareProfile: true }));
  }, [enabled]);

  const skipPayoutSetup = useCallback(() => {
    if (!enabled || !userId) {
      return;
    }
    setSteps((prev) => ({ ...prev, payoutsSkipped: true }));
  }, [enabled, userId]);

  const confirmBusinessTimeZone = useCallback(() => {
    if (!enabled || !userId) {
      return;
    }
    const confirmedTimeZone = steps.businessTimeZone || "";
    storeTimeZoneConfirmation(confirmedTimeZone, userId, activeBusinessId);
    setSteps((prev) => ({ ...prev, confirmedTimeZone }));
  }, [activeBusinessId, enabled, steps.businessTimeZone, userId]);

  // Saves the zone to the business and counts the choice as confirmed.
  // Rejects with the API error so the caller can show it.
  const changeBusinessTimeZone = useCallback(async (timeZone) => {
    if (!enabled || !userId || !activeBusinessId) {
      return;
    }
    const savedTimeZone = await updateBusinessTimeZone({ activeBusinessId, timeZone });
    storeTimeZoneConfirmation(savedTimeZone, userId, activeBusinessId);
    setSteps((prev) => ({
      ...prev,
      businessTimeZone: savedTimeZone,
      confirmedTimeZone: savedTimeZone,
    }));
  }, [activeBusinessId, enabled, userId]);

  return {
    steps,
    progressMeta,
    loading,
    error,
    refresh,
    actions: {
      markShareProfileComplete,
      skipPayoutSetup,
      confirmBusinessTimeZone,
      changeBusinessTimeZone,
    },
  };
};

export default useProfessionalOnboardingProgress;
